<template>
  <v-container class="pa-0">
    <v-row class="d-flex flex-wrap cols">
      <v-col
        :cols="enableFinalCheckpoints ? 4 : 3"
        class="py-0 d-flex flex-column justify-space-between"
      >
        <div class="d-flex align-center">
          <div class="px-1">
            <v-switch
              v-model="referenceCheckpoint"
              density="compact"
              hide-details
              :disabled="isReferenceCheckpointDisabled"
              :label="tkey('checkpoints.reference')"
            />
          </div>
          <div class="px-2 flex-grow-1">
            <v-form v-model="valid" class="d-flex" autocomplete="off" @submit.prevent="">
              <v-text-field
                :key="nameKey"
                v-model.trim="checkpointMeta.name"
                :disabled="!canEditAssortmentCanvas"
                class="text-field"
                :rules="rules"
                hide-details
                @blur="save(checkpoint)"
                @keyup.enter="save(checkpoint)"
                @keyup.esc="discard(checkpoint)"
              />
            </v-form>
          </div>
          <div v-if="enableFinalCheckpoints" class="pa-0 d-flex align-center">
            <template v-if="!isPreviousCheckpoint">
              <v-progress-circular v-if="finalCheckpointLoading" size="24" indeterminate />
              <v-icon v-else primary size="24" @click="toggleFinalCheckpoint(false)">
                {{ checkpoint.isFinalCheckpoint ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </template>
          </div>
        </div>
        <p class="checkpoint-type">
          {{ checkpointGeneratedBy() }} |
          <span style="font-weight: normal">{{
            formatDate(checkpointMeta.createdDate, getDateFormats.longWithTimeAndMeridiem)
          }}</span>
        </p>
      </v-col>

      <v-col :cols="enableFinalCheckpoints ? 6 : 7" class="pa-0 details-col">
        <v-row class="ma-0">
          <v-col cols="4" class="pa-0 detail-sub-col">
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.forecastStart') }}:
              <span> {{ displayForecastParameter('start') }} </span>
            </p>
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.forecastEnd') }}:
              <span> {{ displayForecastParameter('end') }} </span>
            </p>
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.forecastLength') }}:
              <span> {{ displayForecastParameter('length') }} </span>
            </p>
          </v-col>
          <v-col cols="4" class="pa-0 detail-sub-col">
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.sales') }}:
              <span>{{ displayForecastResult('forecastedSales') }}</span>
            </p>
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.margin') }}:
              <span>{{ displayForecastResult('forecastedMargin') }}</span>
            </p>
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.marginPercent') }}:
              <span>{{ displayMarginPercent() }}</span>
            </p>
          </v-col>
          <v-col cols="4" class="pa-0 detail-sub-col">
            <p class="checkpoint-details-title">
              {{ tkey('checkpoints.volume') }}:
              <span>{{ displayForecastResult('forecastedVolume', 'number') }}</span>
            </p>
            <p v-if="isFreshWP" class="checkpoint-details-title">
              {{ tkey('checkpoints.shrink') }}:
              <span>{{ displayForecastResult('forecastedShrinkDollars') }}</span>
            </p>
          </v-col>
        </v-row>
      </v-col>

      <v-col
        align-self="center"
        class="buttons-container d-flex align-center justify-end pa-0 ga-2"
        cols="2"
      >
        <v-btn
          data-id-e2e="btnRevertCheckpoint"
          variant="outlined"
          :disabled="isRevertCheckpointDisabled"
          class="assortment-btn-clear"
          @click="isRevertCheckpointModalOpen = true"
        >
          {{ tkey('checkpoints.revert.action') }}
        </v-btn>
        <v-btn
          data-id-e2e="btnRunForecast"
          primary
          variant="flat"
          :disabled="!canEditAssortmentCanvas"
          :loading="hasForecasterJobRunning"
          @click="isRunForecasterModalOpen = true"
        >
          {{ tkey('checkpoints.runForecast') }}
        </v-btn>
        <v-btn
          v-if="showDelete"
          data-id-e2e="btnDeleteCheckpoint"
          class="delete-button"
          :disabled="!canEditAssortmentCanvas"
          icon
          variant="text"
          @click="isDeleteCheckpointModalOpen = true"
        >
          <v-icon size="24"> $trash </v-icon>
        </v-btn>
        <span v-else style="width: 37px" />
      </v-col>
    </v-row>

    <!-- dialog to display forecaster setup -->
    <v-dialog v-model="isRunForecasterModalOpen" :width="'25%'">
      <BaseDialogCard
        :border="true"
        content-class="pb-0"
        footer-class="hidden"
        @close="cancelForecaster"
      >
        <div class="forecasterSetup">
          <v-row no-gutters class="border-bottom py-3">
            <v-col cols="6" class="pr-4">
              <div class="date-input">
                <div class="wp-scope__date-input-container">
                  <div data-id-e2e="forecasterStartDateField" class="date-input">
                    <span>{{ tkey('forecasterSetupModal.forecastStart') }}:</span>
                    <RtlsCalendarPicker
                      :value="forecasterSetupWeekStartDate"
                      :allowed-dates="allowedStartDates"
                      :display-date-format="getDateFormats.long"
                      :icon-title="tkey('forecasterSetupModal.iconTitle')"
                      @input="handleStartDateChange"
                    />
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-4">
              <span>{{ tkey('forecasterSetupModal.numberOfSlices') }}:</span>
              <AssortmentTextField
                v-model="forecasterSetupNumberOfSlices"
                data-id-e2e="drpForecasterSetupNumberOfSlices"
                variant="filled"
                :rules="numberOfSlicesValidationRules"
                class="text-field"
              />
            </v-col>
          </v-row>
          <v-row no-gutters align="center" class="border-bottom py-3">
            <v-col :cols="4">
              <span>{{ tkey('forecasterSetupModal.forecastSliceLength') }}:</span>
            </v-col>
            <v-col class="d-flex">
              <AssortmentTextField
                v-model="forecasterSetupSliceLength"
                data-id-e2e="drpForecastSliceLength"
                variant="filled"
                :rules="forecasterSetupSliceLengthValidationRules"
                class="text-field"
                @valid="isSliceLengthValid = true"
                @invalid="isSliceLengthValid = false"
              />
            </v-col>
          </v-row>
          <v-row no-gutters align="center" class="py-3">
            <v-col data-id-e2e="fldForecastEndDate" :cols="6" class="d-flex performance-end-date">
              <span class="forecast-end">{{ tkey('forecasterSetupModal.forecastEnd') }}:</span>
              <b>{{ formatDate(forecasterSetupEndDate, getDateFormats.long) }}</b>
              <ErrorTriangle
                class="end-date-error-triangle"
                :errors="{
                  [tkey('errorMessages.endDateBiggerThanWpPerfPeriodEndDate')]: endDateValidation,
                }"
              />
            </v-col>
            <v-col>
              <v-btn
                primary
                class="pull-right"
                :disabled="confirmDisabled"
                @click="startForecaster"
              >
                {{ $t('actions.confirm') }}
              </v-btn>
              <v-btn class="pull-right mr-1" variant="text" @click="cancelForecaster">
                {{ $t('actions.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </BaseDialogCard>
    </v-dialog>

    <!-- revert checkpoint confirmation dialog -->
    <v-dialog v-model="isRevertCheckpointModalOpen" :max-width="400">
      <BaseDialogCard
        :title="tkey('checkpoints.revert.title')"
        :border="true"
        @close="isRevertCheckpointModalOpen = false"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('checkpoints.revert.question') }}</p>
          <strong>{{ tkey('checkpoints.revert.warning') }}</strong>
        </v-card-text>
        <template #footer>
          <v-card-actions>
            <v-btn primary @click="isRevertCheckpointModalOpen = false">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn variant="outlined" color="primary" @click="initRevertCheckpointToCanvas">
              {{ tkey('checkpoints.revert.action') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <!-- delete confirmation dialog -->
    <v-dialog v-model="isDeleteCheckpointModalOpen" :max-width="400">
      <BaseDialogCard>
        <template #header>
          <v-card-text class="text-h6 pb-0 text-center">
            <p>{{ tkey('checkpoints.delete.question') }}</p>
            <strong>{{ tkey('checkpoints.delete.warning') }}</strong>
          </v-card-text>
        </template>
        <template #footer>
          <v-card-actions class="justify-center">
            <v-btn primary class="ma-2" @click="initDelete">
              {{ tkey('checkpoints.delete.action') }}
            </v-btn>
            <v-btn variant="text" class="cancel ma-2" @click="isDeleteCheckpointModalOpen = false">
              {{ $t('actions.cancel') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <!-- final checkpoint mismatch confirmation dialog -->
    <v-dialog v-model="isFinalCheckpointMismatchModalOpen" :max-width="400">
      <BaseDialogCard
        :title="tkey('checkpoints.finalMismatch.title')"
        :border="true"
        @close="isFinalCheckpointMismatchModalOpen = false"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('checkpoints.finalMismatch.message') }}</p>
        </v-card-text>
        <template #footer>
          <v-card-actions>
            <v-btn primary @click="isFinalCheckpointMismatchModalOpen = false">
              {{ tkey('checkpoints.finalMismatch.cancelAction') }}
            </v-btn>
            <v-btn variant="outlined" color="primary" @click="initSetFinalAfterMismatch">
              {{ tkey('checkpoints.finalMismatch.confirmAction') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <!-- dependency tree popup -->
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="checkpointList"
      @close="closeDependencyTreeModal"
      @commit="toggleFinalCheckpoint(true)"
    />
  </v-container>
</template>

<script>
import { cloneDeep, find, get, includes, isEmpty, isNull, map, omit, upperFirst } from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import checkpointTypes from '@enums/checkpoint-types';
import jobapi from '@enums/jobapi';
import scenarioAssortments from '@enums/scenario-assortments';

// composables
import { useInputValidations } from '@/composables/input-validations';
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useCanvasEditability } from '@/composables/use-canvas-editability';
import { useFormatNumber } from '@/composables/use-format-number';
import { useFeature } from '@/composables/use-feature';

// stores
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackagesStore } from '@/stores/workpackages';

export default {
  props: {
    checkpointId: {
      required: true,
      type: String,
    },
    remainingCheckpoints: {
      required: true,
      type: Array,
    },
  },
  setup() {
    const { canEditAssortmentCanvas } = usePermission();
    const { canvasCanBeEdited } = useCanvasEditability();
    const { enableFinalCheckpoints } = useFeature();

    const {
      calculateEndDate,
      calculateStartDate,
      formatDateForSave,
      formatDate,
      getUTCFromDate,
      getIsoWeekday,
    } = useDayjs();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { tkey } = useInternationalization('assortmentCanvasPage');

    const { formatNumber } = useFormatNumber();

    return {
      // flags
      enableFinalCheckpoints,

      // permissions
      canEditAssortmentCanvas,
      canvasCanBeEdited,
      // date utils
      calculateEndDate,
      calculateStartDate,
      formatDateForSave,
      getUTCFromDate,
      getIsoWeekday,
      // validations
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      // i18n
      tkey,
      // formatters
      formatNumber,
      formatDate,
    };
  },

  data() {
    return {
      forecasterSetupWeekStartDate: null,
      forecasterSetupSliceLength: null,
      forecasterSetupNumberOfSlices: null,
      dependencyTreeModalOpen: false,
      rules: [this.required, this.isUniqueCheckpointName],
      nameKey: false,
      numberOfSlicesValidationRules: [
        value => (value ? true : this.tkey('errorMessages.selectForecastNumberOfSlices')),
        this.isInteger,
        this.isPositive,
      ],
      forecasterSetupSliceLengthValidationRules: [
        value => (value ? true : this.tkey('errorMessages.selectForecastSliceLength')),
        this.isInteger,
        this.isPositive,
        value =>
          value <= this.selectedWorkpackage.performanceDateWeeks
            ? true
            : this.tkey('errorMessages.forecastSliceLengthExceedsPerformancePeriod'),
      ],
      isSliceLengthValid: true,
      valid: true,
      forecastParametersDisplayFunctions: {
        start: () =>
          this.getUTCFromDate(this.forecastParameters.weekStartDate).format(
            this.getDateFormats.long
          ),
        end: () => this.getUTCFromDate(this.forecastEndDate).format(this.getDateFormats.long),
        length: () => this.tkey('checkpoints.forecastLengthText', this.forecastParameters),
      },
      confirmDisabled: false,
      isRevertCheckpointModalOpen: false,
      isDeleteCheckpointModalOpen: false,
      isRunForecasterModalOpen: false,
      isFinalCheckpointMismatchModalOpen: false,
      dependencyTreeFeedback: {},
      isSaving: false,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'selectedReferenceCheckpoint',
      'selectedAssortmentCanvas',
      'checkpoints',
      'canvases',
      'finalCheckpointLoading',
    ]),
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'getSelectedWorkpackageExcludedDates',
      'isFreshWP',
    ]),
    ...mapState(useContextStore, [
      'showNotImplemented',
      'getDateFormats',
      'getDefaultReverseFormat',
      'getClientConfig',
    ]),

    checkpoint() {
      return cloneDeep(find(this.checkpoints, c => c._id === this.checkpointId));
    },

    checkpointMeta() {
      return this.checkpoint.checkpoint;
    },

    forecastParameters() {
      return this.checkpoint.forecastParameters;
    },

    forecastResults() {
      return this.checkpoint.forecastResults;
    },

    defaultForecasterSetupSliceLength() {
      if (
        get(
          this.getClientConfig,
          'features.forecaster.usePerformanceDateWeeksAsDefaultSliceLength',
          false
        )
      ) {
        return this.selectedWorkpackage.performanceDateWeeks;
      }

      return get(
        this.getClientConfig,
        'features.forecaster.defaultForecasterSetupSliceLength',
        null
      );
    },

    defaultForecasterSetupNumberOfSlices() {
      return get(
        this.getClientConfig,
        'features.forecaster.defaultForecasterSetupNumberOfSlices',
        null
      );
    },

    referenceCheckpoint: {
      get() {
        return this.isReferenceCheckpoint;
      },
      set() {
        const newValue = this.isReferenceCheckpoint ? null : this.checkpointId;

        this.setSelectedReferenceCheckpoint(newValue);
      },
    },

    isReferenceCheckpoint() {
      return this.selectedReferenceCheckpoint
        ? this.checkpointId === this.selectedReferenceCheckpoint
        : false;
    },

    isObservedCheckpoint() {
      return this.checkpointMeta.type === checkpointTypes.observedAssortment;
    },

    isPreviousCheckpoint() {
      return this.checkpointMeta.type === checkpointTypes.previousAssortment;
    },

    isRevertCheckpointDisabled() {
      return (
        this.isObservedCheckpoint ||
        this.isPreviousCheckpoint ||
        this.checkpoint.isNotRevertable ||
        this.isEditingDisabled
      );
    },

    isReferenceCheckpointDisabled() {
      return (
        this.isObservedCheckpoint || this.isPreviousCheckpoint || !this.canEditAssortmentCanvas
      );
    },

    forecasterSetupEndDate() {
      const excludedDatesCount = this.getWorkpackageExcludedDatesCount(
        this.forecasterSetupWeekStartDate
      );
      // The end date to show in the run forecaster popup
      return this.calculateEndDate({
        startDate: this.forecasterSetupWeekStartDate,
        weeks: this.forecasterSetupNumberOfForecasterWeeks + excludedDatesCount,
        dateFormat: this.getDefaultReverseFormat,
      });
    },

    forecastEndDate() {
      const excludedDatesCount = this.getWorkpackageExcludedDatesCount(
        this.forecastParameters.weekStartDate
      );
      // The end date to show in the checkpoint
      return this.calculateEndDate({
        startDate: this.forecastParameters.weekStartDate,
        weeks: this.numberOfForecasterWeeks + excludedDatesCount,
        dateFormat: this.getDefaultReverseFormat,
      });
    },

    showDelete() {
      return ![
        checkpointTypes.observedAssortment,
        checkpointTypes.startingAssortment,
        checkpointTypes.previousAssortment,
      ].includes(this.checkpointMeta.type);
    },

    forecasterJobStatus() {
      return this.checkpoint.jobs && this.checkpoint.jobs.forecaster
        ? this.checkpoint.jobs.forecaster.status
        : null;
    },

    hasForecasterJobRunning() {
      return this.forecasterJobStatus
        ? !includes(jobapi.jobFinishedStatuses, this.forecasterJobStatus.toLowerCase())
        : false;
    },

    forecasterSetupNumberOfForecasterWeeks() {
      const sliceValue = this.forecasterSetupSliceLength;
      return this.forecasterSetupNumberOfSlices * sliceValue;
    },

    numberOfForecasterWeeks() {
      const sliceValue = this.forecastParameters.sliceLength;
      return this.forecastParameters.numberOfSlices * sliceValue;
    },

    allowedStartDates() {
      const { performanceStartDate, performanceEndDate, performanceExcludedDates } =
        this.selectedWorkpackage;
      const parsedExcludedDates = map(
        performanceExcludedDates || [],
        ped => this.getUTCFromDate(ped).format('YYYY-MM-DD') // no need to use config because this is for local comparison only
      );
      return date => {
        const parsedDate = this.getUTCFromDate(date);
        // [ is inclusive. Forecastor start date can be >= performanceStartDate.
        // ) is exclusive. Forecastor start date must be < performanceEndDate
        // shouldn't matter about exclusive portion because end dates should always be Sundays and this must be Monday.
        // getIsoWeekday uses local (non-UTC) parsing to avoid timezone-induced off-by-one day errors if the users timezone is ahead of UTC
        return (
          this.getIsoWeekday(date) === 1 &&
          parsedDate.isBetween(performanceStartDate, performanceEndDate, 'day', '[)') &&
          parsedExcludedDates.includes(date) === false
        );
      };
    },

    endDateValidation() {
      if (isNull(this.forecasterSetupEndDate)) {
        return true;
      }
      return (
        this.getUTCFromDate(this.selectedWorkpackage.performanceEndDate) >=
        this.getUTCFromDate(this.forecasterSetupEndDate)
      );
    },

    isEditingDisabled() {
      return !this.canvasCanBeEdited;
    },
  },

  watch: {
    forecasterSetupNumberOfSlices(val) {
      this.updateConfirmDisabledFlag(val, this.endDateValidation);
    },

    endDateValidation(val) {
      this.updateConfirmDisabledFlag(this.forecasterSetupNumberOfSlices, val);
    },

    forecasterSetupSliceLength() {
      this.updateConfirmDisabledFlag(this.forecasterSetupNumberOfSlices, this.endDateValidation);
    },
  },

  async created() {
    this.forecasterSetupSliceLength =
      this.forecastParameters.sliceLength || this.defaultForecasterSetupSliceLength;
    this.forecasterSetupNumberOfSlices =
      this.forecastParameters.numberOfSlices || this.defaultForecasterSetupNumberOfSlices;
    this.setInitialForecasterStartDate();
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'updateCheckpoint',
      'deleteCheckpoint',
      'fetchCheckpoints',
      'fetchCanvas',
      'runForecasterJob',
      'fetchCanvases',
      'updateAssortmentCanvas',
      'deleteAssortmentCanvas',
      'revertCheckpointToCanvas',
      'setSelectedReferenceCheckpoint',
      'setFinalCheckpoint',
    ]),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useSnackbarStore, ['showSnackbar']),

    displayForecastResult(key, format = 'currencyRounded') {
      if (this.hasForecasterJobRunning) {
        return this.tkey('checkpoints.calculating');
      }
      return isEmpty(this.forecastResults)
        ? this.$t('general.notAvailable')
        : this.formatNumber({ number: this.forecastResults[key], format });
    },

    displayMarginPercent() {
      if (this.hasForecasterJobRunning) {
        return this.tkey('checkpoints.calculating');
      }
      if (isEmpty(this.forecastResults)) {
        return this.$t('general.notAvailable');
      }
      const { forecastedMargin, forecastedSales } = this.forecastResults;
      if (!forecastedSales) {
        return this.$t('general.notAvailable');
      }
      return this.formatNumber({
        number: forecastedMargin / forecastedSales,
        format: 'percentagesWithTwoDecimals',
      });
    },

    displayForecastParameter(key) {
      if (isEmpty(this.forecastParameters)) {
        return this.$t('general.notAvailable');
      }
      return this.forecastParametersDisplayFunctions[key]();
    },

    setInitialForecasterStartDate() {
      if (!this.selectedWorkpackage) return;
      // If there's no defined number of weeks to forecast for,
      // set performance start date as forecaster start date.
      if (!this.forecasterSetupNumberOfForecasterWeeks) {
        this.forecasterSetupWeekStartDate = this.getUTCFromDate(
          this.selectedWorkpackage.performanceStartDate
        ).format(this.getDefaultReverseFormat);
      } else {
        this.forecasterSetupWeekStartDate = this.calculateStartDate({
          endDate: this.selectedWorkpackage.performanceEndDate,
          weeks: this.forecasterSetupNumberOfForecasterWeeks,
          dateFormat: this.getDefaultReverseFormat,
        });
      }

      let isForecasterSetupWeekStartDateInvalid = true;

      // If forecasterSetupWeekStartDate is not valid we set forecasterSetupWeekStartDate as nearest valid date
      while (isForecasterSetupWeekStartDateInvalid) {
        if (this.allowedStartDates(this.forecasterSetupWeekStartDate)) {
          isForecasterSetupWeekStartDateInvalid = false;
        } else {
          this.forecasterSetupWeekStartDate = this.getUTCFromDate(this.forecasterSetupWeekStartDate)
            .add(7, 'days')
            .format(this.getDefaultReverseFormat);
        }
      }

      if (!this.endDateValidation) {
        this.forecasterSetupNumberOfSlices = Math.round(
          this.getUTCFromDate(this.selectedWorkpackage.performanceEndDate).diff(
            this.getUTCFromDate(this.forecasterSetupWeekStartDate),
            'week'
          ) / 2
        );
      }
    },

    handleStartDateChange(value) {
      this.forecasterSetupWeekStartDate = value;
    },

    isUniqueCheckpointName(value) {
      if (this.remainingCheckpoints.filter(cp => cp.checkpoint.name === value.trim()).length) {
        return this.$t('validationErrors.unique', [this.tkey('checkpoint')]);
      }
      return true;
    },

    checkpointGeneratedBy() {
      const checkpointType = this.checkpointMeta.type;
      let text = `${upperFirst(
        scenarioAssortments.CHECKPOINT_TYPE[checkpointType] !==
          scenarioAssortments.CHECKPOINT_TYPE.user
          ? scenarioAssortments.CHECKPOINT_TYPE.tool
          : scenarioAssortments.CHECKPOINT_TYPE.user
      )}
      ${this.tkey('checkpoints.generatedCheckpoint')}`;
      if (this.isObservedCheckpoint) {
        text = `${text} (${this.tkey('checkpoints.observedCheckpoint')})`;
      }
      return text;
    },

    async save(item) {
      if (!this.valid) return false;
      const updates = {
        ...omit(item, 'checkpoint', 'id'),
        ...{ checkpointMeta: item.checkpoint },
      };
      await this.updateCheckpoint({ checkpointId: item._id, updates });
      await this.fetchCheckpoints();
      this.nameKey = !this.nameKey;
    },

    getErrorTranslation(errors) {
      return errors.map(error =>
        this.tkey(scenarioAssortments.API_ERRORS[error.code] || 'errors.generalErrorMessage')
      );
    },

    async initDelete() {
      this.confirmDelete(this.checkpointId);
    },

    async initRevertCheckpointToCanvas() {
      await this.revertCheckpointToCanvas({ checkpointId: this.checkpointId });
      this.isRevertCheckpointModalOpen = false;
      this.$emit('close');
    },

    async confirmDelete(canvasId) {
      const checkpointToDelete = this.checkpoints.find(cp => cp._id === canvasId);
      const { errors } = await this.deleteCheckpoint({ canvasId });
      if (errors) {
        return this.getErrorTranslation(errors).forEach(err => this.showError(err));
      }
      const refreshTasks = [this.fetchCheckpoints()];
      if (checkpointToDelete.isFinalCheckpoint) {
        // When a final checkpoint is deleted, the live canvas is unlocked on the server
        // but we need to re-fetch that canvas to update the UI.
        refreshTasks.push(
          this.fetchCanvas({
            canvasId: this.selectedAssortmentCanvas._id,
            forceSelectedCanvasUpdate: true,
          })
        );
      }
      await Promise.all(refreshTasks);
    },

    showError(error) {
      this.showSnackbar({ content: error });
    },

    cancelForecaster() {
      this.isRunForecasterModalOpen = false;
    },

    async startForecaster() {
      const { performanceStartDate, performanceEndDate } = this.selectedWorkpackage;
      if (
        this.getUTCFromDate(this.forecasterSetupWeekStartDate) <
          this.getUTCFromDate(performanceStartDate) ||
        this.getUTCFromDate(performanceEndDate) < this.getUTCFromDate(this.forecasterSetupEndDate)
      ) {
        this.showError(this.tkey('forecasterSetupModal.periodOutOfBoundsError'));
        return;
      }

      await this.runForecasterJob({
        checkpointId: this.checkpoint._id,
        scenarioId: this.$route.params.scenarioId,
      });
      const updates = {
        forecastParameters: {
          numberOfSlices: parseInt(this.forecasterSetupNumberOfSlices, 10),
          sliceLength: parseInt(this.forecasterSetupSliceLength, 10),
          weekStartDate: this.formatDateForSave(
            this.forecasterSetupWeekStartDate,
            this.getDefaultReverseFormat
          ),
        },
      };
      await this.updateCheckpoint({ checkpointId: this.checkpoint._id, updates });
      await this.fetchCheckpoints();
      this.cancelForecaster();
    },

    updateConfirmDisabledFlag(numberOfSlices, endDateValidation) {
      if (numberOfSlices > 0 && endDateValidation && this.isSliceLengthValid) {
        this.confirmDisabled = false;
        return;
      }
      this.confirmDisabled = true;
    },

    getWorkpackageExcludedDatesCount(forecasterDate) {
      return this.getSelectedWorkpackageExcludedDates(forecasterDate).length;
    },

    async checkDependencyTreeForFeedback(isFinalCheckpoint, commit) {
      const results = await this.triggerDependencyTree({
        params: {
          change: 'checkpointModified',
          updates: {
            rowLevel: [
              {
                checkpointId: this.checkpoint._id,
                isFinalCheckpoint,
              },
            ],
          },
          commit,
        },
      });

      const { needsFeedback, output } = results;
      if (needsFeedback) {
        // open the modal
        this.dependencyTreeFeedback = output;
        this.dependencyTreeModalOpen = true;
      }

      return needsFeedback;
    },

    async initSetFinalAfterMismatch() {
      this.isFinalCheckpointMismatchModalOpen = false;
      await this.revertCheckpointToCanvas({ checkpointId: this.checkpointId });
      await this.toggleFinalCheckpoint(true);
    },

    async toggleFinalCheckpoint(commit = false) {
      this.isSaving = true;
      const isFinalCheckpoint = !this.checkpoint.isFinalCheckpoint;

      try {
        const needsFeedback = await this.checkDependencyTreeForFeedback(isFinalCheckpoint, commit);
        if (needsFeedback) {
          this.isSaving = false;
          return;
        }

        await this.setFinalCheckpoint({
          checkpointId: this.checkpointId,
          value: isFinalCheckpoint,
        });
        this.isSaving = false;
      } catch (error) {
        this.isSaving = false;

        const isFinalCheckpointMismatch =
          isFinalCheckpoint &&
          get(error, 'response.data.messageKey') ===
            'assortmentCanvasPage.checkpoints.finalMismatch.message';

        if (isFinalCheckpointMismatch) {
          this.isFinalCheckpointMismatchModalOpen = true;
          return;
        }

        throw error;
      }
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.v-container {
  padding: 8px;

  .assortment-btn-clear {
    color: $assortment-primary-colour;
  }

  .error-box {
    margin-left: 10px;
  }

  .show-details {
    display: flex;
    align-items: center;
    position: absolute;
    left: 50%;
    span {
      font-weight: bold;
      color: $assortment-primary-colour;
    }

    .v-icon {
      transform: translate(0px, 0.5px);
    }
  }

  .detail-sub-col {
    min-width: 0;
  }

  .checkpoint-details-title {
    font-weight: bold;
    font-size: 1.2rem;
    margin: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;

    span {
      font-weight: normal;
    }
  }

  .buttons-container {
    flex-shrink: 0 !important;
    flex-wrap: nowrap;
    white-space: nowrap;
  }
}

.checkpoint-type {
  margin: 0;
  font-weight: bold;
}

.forecasterSetup {
  span {
    font-weight: bold;
  }
  .border-bottom {
    border-bottom: $assortment-border-colour 1px solid;
    padding-bottom: 10px;
  }
  .container {
    padding: 0 !important;
  }
}

:deep(.v-label) {
  color: black;
}

.end-date-error-triangle {
  padding-left: 8px;
}

.performance-end-date {
  font-size: 12px;
}

.forecast-end {
  padding-right: 1px;
}
</style>
